import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../../services/apiUtils";
import imageCompression from "browser-image-compression";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../../routes/constant";
import { extractObjectId } from "../../../utils/utils";
import "./AddPlaylist.css";
import {
  FaMusic,
  FaListAlt,
  FaAlignLeft,
  FaGlobe,
  FaLock,
  FaGuitar,
  FaTags,
  FaImage,
  FaCloudUploadAlt,
  FaUserTag,
  FaSearch,
  FaTimes,
  FaPlus,
} from "react-icons/fa";

const AddPlaylist = () => {
  const [playlistData, setPlaylistData] = useState({
    playlist_name: "",
    playlist_description: "",
    playlist_type: "Public",
  });

  const [playlist_image, setPlaylistImage] = useState(null);
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Artist tagging state
  const [taggedArtists, setTaggedArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);

  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
  };

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
        if (response && response.success) {
          setGenres(response.data);
        } else {
          toast.error("Failed to fetch genres.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching genres.");
        console.error("Error fetching genres:", error);
      }
    };

    fetchGenres();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPlaylistData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPlaylistImage(file);
    }
  };

  // Handle multiple genre selection
  const handleGenreToggle = (genre) => {
    setSelectedGenres((prev) => {
      const isSelected = prev.find(g => g._id === genre._id);
      if (isSelected) {
        return prev.filter(g => g._id !== genre._id);
      } else {
        return [...prev, genre];
      }
    });
  };

  // Handle tag input
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Artist search handler
  const handleArtistSearch = async (searchTerm) => {
    setArtistSearch(searchTerm);
    if (searchTerm.length < 2) {
      setArtistResults([]);
      setShowArtistDropdown(false);
      return;
    }

    try {
      const response = await allAPiServicesCall.getSearchArtist(
        { query: searchTerm },
        getAuthConfig(),
        navigate
      );
      if (response?.data) {
        setArtistResults(response.data);
        setShowArtistDropdown(true);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    }
  };

  const handleAddArtist = (artist) => {
    if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setTaggedArtists([...taggedArtists, artist]);
    }
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  const handleRemoveArtist = (artistId) => {
    setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  const validateForm = () => {
    if (!playlistData.playlist_name || !playlistData.playlist_description || !playlist_image) {
      toast.error("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const compressedImage = await imageCompression(playlist_image, options);
      const formData = new FormData();
      formData.append("image", compressedImage);

      const imageResponse = await allAPiServicesCall.uploadPlaylistImage(
        formData,
        getFormDataConfig()
      );

      const playlistDataPayload = {
        ...playlistData,
        playlist_image: imageResponse.data.path,
        playlist_genres: selectedGenres.map(g => g.genreName),
        playlist_tags: tags,
        taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id)),
      };

      const response = await allAPiServicesCall.createAdminPlaylist(
        playlistDataPayload,
        getAuthConfig()
      );

      if (response && response.success) {
        toast.success("Playlist created successfully!");
        resetForm();
        navigate(ROUTE_CONSTANT.COMMON.PLAYLIST);
      }
    } catch (error) {
      toast.error("An error occurred while creating the playlist.");
      console.error("Error creating playlist:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setPlaylistData({
      playlist_name: "",
      playlist_description: "",
      playlist_type: "Public",
    });
    setSelectedGenres([]);
    setTags([]);
    setTagInput('');
    setPlaylistImage(null);
    setTaggedArtists([]);
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  return (
    <CommonLayout>
      <div className="create-playlist-page">
        {/* Header Section */}
        <div className="playlist-header">
          <div className="playlist-header-icon">
            <FaMusic />
          </div>
          <div className="playlist-header-text">
            <h1 className="playlist-title">Create New Playlist</h1>
            <p className="playlist-subtitle">
              Build a curated collection of songs for your users
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="playlist-form-card">
          <form onSubmit={handleSubmit}>
            {/* Playlist Name & Type Row */}
            <div className="playlist-form-section">
              <div className="playlist-form-row">
                <div>
                  <label className="playlist-form-label">
                    <FaListAlt className="playlist-form-label-icon" />
                    Playlist Name *
                  </label>
                  <input
                    type="text"
                    className="playlist-input"
                    placeholder="Enter playlist name"
                    name="playlist_name"
                    value={playlistData.playlist_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="playlist-form-label">
                    {playlistData.playlist_type === "Public" ? (
                      <FaGlobe className="playlist-form-label-icon" />
                    ) : (
                      <FaLock className="playlist-form-label-icon" />
                    )}
                    Playlist Type
                  </label>
                  <select
                    className="playlist-input playlist-select"
                    name="playlist_type"
                    value={playlistData.playlist_type}
                    onChange={handleChange}
                  >
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="playlist-form-section">
              <label className="playlist-form-label">
                <FaAlignLeft className="playlist-form-label-icon" />
                Description *
              </label>
              <textarea
                className="playlist-input playlist-textarea"
                placeholder="Enter playlist description"
                name="playlist_description"
                value={playlistData.playlist_description}
                onChange={handleChange}
                required
              />
            </div>

            {/* Genres & Tags Row */}
            <div className="playlist-form-section">
              <div className="playlist-form-row">
                {/* Genres */}
                <div>
                  <label className="playlist-form-label">
                    <FaGuitar className="playlist-form-label-icon" />
                    Genres (Select multiple)
                  </label>
                  <div className="playlist-genres-container">
                    {genres.map((genre, index) => (
                      <div
                        key={genre._id}
                        className="playlist-genre-item"
                        onClick={() => handleGenreToggle(genre)}
                      >
                        <input
                          type="checkbox"
                          className="playlist-genre-checkbox"
                          id={`genre-checkbox-${index}`}
                          checked={selectedGenres.some(g => g._id === genre._id)}
                          onChange={() => {}}
                        />
                        <label
                          className="playlist-genre-label"
                          htmlFor={`genre-checkbox-${index}`}
                        >
                          {genre.genreName}
                        </label>
                      </div>
                    ))}
                  </div>
                  {selectedGenres.length > 0 && (
                    <div className="playlist-selected-genres">
                      {selectedGenres.map((genre) => (
                        <span key={genre._id} className="playlist-genre-badge">
                          {genre.genreName}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="playlist-form-label">
                    <FaTags className="playlist-form-label-icon" />
                    Tags
                  </label>
                  <input
                    type="text"
                    className="playlist-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Type and press Enter to add tags"
                  />
                  {tags.length > 0 && (
                    <div className="playlist-tags-display">
                      {tags.map((tag, index) => (
                        <span key={index} className="playlist-tag">
                          #{tag}
                          <button
                            type="button"
                            className="playlist-tag-remove"
                            onClick={() => handleRemoveTag(tag)}
                          >
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div className="playlist-form-section">
              <label className="playlist-form-label">
                <FaImage className="playlist-form-label-icon" />
                Playlist Cover Image *
              </label>
              <div className="playlist-image-upload">
                <input
                  type="file"
                  id="playlist-image-input"
                  className="playlist-image-input"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {!playlist_image ? (
                  <label htmlFor="playlist-image-input" className="playlist-image-label">
                    <div className="playlist-image-icon">
                      <FaCloudUploadAlt />
                    </div>
                    <div className="playlist-image-text">
                      <p className="playlist-image-text-main">Click to upload image</p>
                      <p className="playlist-image-text-sub">PNG, JPG up to 10MB</p>
                    </div>
                  </label>
                ) : (
                  <div className="playlist-image-preview">
                    <img
                      src={URL.createObjectURL(playlist_image)}
                      alt="Selected playlist"
                    />
                    <p className="playlist-image-name">{playlist_image.name}</p>
                    <button
                      type="button"
                      className="playlist-image-change"
                      onClick={() => document.getElementById('playlist-image-input').click()}
                    >
                      Change Image
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Artist Tagging */}
            <div className="playlist-form-section">
              <label className="playlist-form-label">
                <FaUserTag className="playlist-form-label-icon" />
                Tag Artists
              </label>
              <div className="playlist-artist-search">
                <input
                  type="text"
                  className="playlist-input"
                  value={artistSearch}
                  onChange={(e) => handleArtistSearch(e.target.value)}
                  placeholder="Search for artists to tag..."
                />
                {showArtistDropdown && artistResults.length > 0 && (
                  <div className="playlist-artist-dropdown">
                    {artistResults.map((artist) => (
                      <div
                        key={extractObjectId(artist._id)}
                        className="playlist-artist-item"
                        onClick={() => handleAddArtist(artist)}
                      >
                        <FaSearch style={{ color: '#EC4899', fontSize: '0.75rem' }} />
                        <span className="playlist-artist-name">{artist.artist_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {taggedArtists.length > 0 && (
                <div className="playlist-tagged-artists">
                  {taggedArtists.map((artist) => (
                    <span key={extractObjectId(artist._id)} className="playlist-artist-badge">
                      {artist.artist_name}
                      <button
                        type="button"
                        className="playlist-artist-remove"
                        onClick={() => handleRemoveArtist(extractObjectId(artist._id))}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="playlist-submit-section">
              <button
                type="submit"
                className="playlist-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaPlus /> Create Playlist
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddPlaylist;
